import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { BookOpen, Sparkles, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { benvenuto, gruppoBenvenuto } from "@/data/benvenuto";

/** Le parole in **grassetto** diventano il titolo della riga: il testo resta leggibile anche stampato. */
const Riga = ({ testo }) => {
  const m = testo.match(/^\*\*(.+?)\*\*\s*(.*)$/);
  return (
    <li className="flex gap-2.5">
      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
      <span className="min-w-0 text-sm text-slate-700">
        {m ? <><span className="font-semibold text-slate-900">{m[1]}</span> {m[2]}</> : testo}
      </span>
    </li>
  );
};

/** Contenuto della guida per il ruolo di chi guarda: usato dal benvenuto e dalla pagina Guida rapida. */
export const GuidaContenuto = ({ passo, setPasso }) => {
  const { user } = useApp();
  const { lang } = useI18n();
  const b = benvenuto(lang);
  const gruppo = gruppoBenvenuto(user);

  if (gruppo === "titolare") {
    const s = b.titolare.slides[passo];
    return (
      <div data-testid="guida-titolare">
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-amber-600">
          {b.ui.of.replace("{a}", passo + 1).replace("{b}", b.titolare.slides.length)}
        </p>
        <h2 className="mt-1 font-head text-xl font-bold">{s.t}</h2>
        <p className="mt-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{s.why}</p>
        <ul className="mt-4 space-y-2.5">{s.p.map((x) => <Riga key={x} testo={x} />)}</ul>
        <div className="mt-5 flex gap-2">
          {passo > 0 && (
            <Button variant="outline" className="h-11" onClick={() => setPasso(passo - 1)} data-testid="guida-back">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> {b.ui.back}
            </Button>
          )}
          {passo < b.titolare.slides.length - 1 && (
            <Button className="h-11 flex-1" onClick={() => setPasso(passo + 1)} data-testid="guida-next">
              {b.ui.next} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  const d = b[gruppo];
  return (
    <div data-testid={`guida-${gruppo}`}>
      <h2 className="font-head text-xl font-bold">{d.hello}</h2>
      <p className="mt-1 text-sm text-slate-500">{d.sub}</p>
      <ul className="mt-4 space-y-3">{d.p.map((x) => <Riga key={x} testo={x} />)}</ul>
      <p className="mt-4 rounded-lg bg-slate-100 p-3 text-xs text-slate-600">{d.foot}</p>
    </div>
  );
};

/** Benvenuto al primo accesso: una volta sola per persona, poi resta in «Guida rapida». */
export const Benvenuto = () => {
  const { user, showcase, demoMode } = useApp();
  const { lang } = useI18n();
  const nav = useNavigate();
  const b = benvenuto(lang);
  const gruppo = gruppoBenvenuto(user);
  const chiave = `rh_welcome_${user?.id || user?.name || gruppo}`;
  const [open, setOpen] = useState(false);
  const [passo, setPasso] = useState(0);

  useEffect(() => {
    if (!user?.role) return;
    // Chi apre la demo da un link deve vedere subito il gestionale: la guida resta in «Guida rapida»
    if (demoMode) return;
    if (localStorage.getItem(chiave)) return;
    setOpen(true);
  }, [user, chiave, demoMode]);

  const chiudi = () => {
    localStorage.setItem(chiave, "1");
    setOpen(false);
  };

  const ultimo = gruppo !== "titolare" || passo === b.titolare.slides.length - 1;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && chiudi()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="benvenuto-dialog">
        <DialogTitle className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">
          <Sparkles className="h-3.5 w-3.5 text-amber-500" />
          {gruppo === "titolare" ? b.titolare.hello : `${b[gruppo].hello}, ${user?.name?.split(" ")[0] || ""}`}
        </DialogTitle>
        {gruppo === "titolare" && <p className="mt-1 text-sm text-slate-500">{b.titolare.sub}</p>}
        <div className="mt-3">
          <GuidaContenuto passo={passo} setPasso={setPasso} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button className="h-12 flex-1" onClick={chiudi} data-testid="benvenuto-done">
            <Check className="mr-1.5 h-4 w-4" /> {ultimo ? b.ui.done : b.ui.skip}
          </Button>
          {!showcase && (
            <Button
              variant="outline"
              className="h-12"
              data-testid="benvenuto-manual"
              onClick={() => {
                chiudi();
                nav("/manuale");
              }}
            >
              <BookOpen className="mr-1.5 h-4 w-4" /> {b.ui.manual}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
